import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import type { Environment } from '../../../../server/src/models';
import type { ImageVersion } from '../../../../server/src/models/environment';
import { trpc } from '../../../utils/trpc';
import WizardFinalStep, { MetaInfo } from './WizardFinalStep';
import WizardStep from './WizardStep';

const CreateEnvWizard = () => {
  const STEPS = 4;

  const [step, setStep] = useState(1);
  const [frontendVersion, setFrontendVersion] = useState<ImageVersion | undefined>(undefined);
  const [backendVersion, setBackendVersion] = useState<ImageVersion | undefined>(undefined);
  const [stockerVersion, setStockerVersion] = useState<ImageVersion | undefined>(undefined);
  const [metaInfo, setMetaInfo] = useState<MetaInfo>();
  const [envCreating, setEnvCreating] = useState(false);

  const navigate = useNavigate();

  const utils = trpc.useContext();
  const createEnv = trpc.env.create.useMutation({
    onSuccess: async () => {
      await utils.env.list.invalidate();
    }
  });

  const createStack = trpc.stack.create.useMutation();

  // We have transitioned through all the steps, create an environment object
  useEffect(() => {
    if (step !== 5 || envCreating) return;
    setEnvCreating(true);
    const envId = uuidv4();

    createStack.mutate(
      {
        name: `app-${envId.split('-')[1]}`,
        frontendTag: frontendVersion!.tag,
        backendTag: backendVersion!.tag,
        stockerTag: stockerVersion!.tag
      },
      {
        onSuccess: ({ id }) => {
          const environment: Environment = {
            id: envId,
            name: metaInfo!.name,
            stack: {
              name: `app-${envId.split('-')[1]}`,
              type: 'compose-app',
              frontendImage: frontendVersion!,
              backendImage: backendVersion!,
              stockerImage: stockerVersion!,
              stackState: 'ACTIVE',
              id: id,
              createdAt: new Date().toISOString()
            },
            createdAt: new Date().toISOString(),
            state: 'CREATED'
          };

          createEnv.mutate(environment, {
            onSuccess: () => {
              console.log('Environment created!');
              setEnvCreating(false);
              navigate(`/dev/env/${environment.id}`);
            },
            onError: (error) => {
              console.error(error);
            }
          });
        }
      }
    );
  }, [step, backendVersion, frontendVersion, metaInfo, stockerVersion, envCreating]);

  return (
    <div className="flex flex-row">
      <div className="md:basis-3/12 md:block hidden" />

      {step === 1 && (
        <WizardStep
          steps={STEPS}
          step={step}
          stepMessage="Select frontend image"
          setStep={setStep}
          repository="bioplan-frontend-dev"
          setVersionHandler={setFrontendVersion}
        />
      )}

      {step === 2 && (
        <WizardStep
          steps={STEPS}
          step={step}
          stepMessage="Select backend image"
          setStep={setStep}
          repository="bioplan-domain-dev"
          setVersionHandler={setBackendVersion}
        />
      )}

      {step === 3 && (
        <WizardStep
          steps={STEPS}
          step={step}
          stepMessage="Select optimizer image"
          setStep={setStep}
          repository="stocker-dev"
          setVersionHandler={setStockerVersion}
        />
      )}

      {step === 4 && <WizardFinalStep steps={STEPS} step={step} setStep={setStep} setMetaInfo={setMetaInfo} />}

      {step > 4 && (
        <>
          <div className="basis-11/12 md:basis-7/12 md:px-0 px-4">
            <div className="border-b-2 py-4">
              <div className="uppercase tracking-wide text-xs font-bold text-gray-500 mb-1 leading-tight">
                Steps completed
              </div>
              <div className="flex flex-row items-center justify-between py-2">
                <div className="basis-1/2">
                  <div className="text-lg font-bold text-gray-700 leading-tight left">Creating environment ...</div>
                </div>

                <svg
                  role="status"
                  className="w-8 h-8 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CreateEnvWizard;
